
import { supabase } from '../lib/supabase'
import { normalizeEmail } from '../utils/emailValidation'
import { getAuthUser, invalidateAuthUser, clearAuthUser } from '../hooks/useAuthUser'
import { invokeAdminAction, invokeStaffLogin } from '../utils/supabaseFunctions'
import {
    buildStaffSessionFromEntry,
    fetchStaffListFromServer,
    findStaffEntry,
    staffSessionPermissionSignature,
    type StaffListEntry,
} from '../utils/staffPermissionSync'
import { getWindowLocaleStorage, removeWindowLocaleStorage, setWindowLocaleStorage } from '../utils/windowSessionStorage'
import { clearImpersonationStorage, getImpersonateTenantRaw, getImpersonationSource, parseImpersonationTenantId, resolveAuthorizedImpersonation } from '../utils/impersonationSession'
import { isStaffSessionJwt, jwtPayload } from '../utils/supabaseStorageAuth'
import { otpService } from './otp.service'

export interface AuthUser {
    id: string
    email: string
    role: 'super_admin' | 'customer' | 'manager' | 'associate_manager'
    tenant_id?: string
    metadata?: any
}

/** Activity feed "cards per row" (1–4) in `profiles.theme_settings.activity_feed_columns`. */
export function parseActivityFeedColumnsFromThemeSettings(themeSettings: unknown): number | null {
    if (!themeSettings || typeof themeSettings !== 'object' || Array.isArray(themeSettings)) return null
    const raw = (themeSettings as Record<string, unknown>).activity_feed_columns
    const x = typeof raw === 'number' ? raw : Number(raw)
    if (!Number.isFinite(x)) return null
    const i = Math.round(x)
    if (i < 1 || i > 4) return null
    return i
}

/**
 * Find a staff session string from any available storage source.
 * Checks: sessionStorage → window-scoped localStorage → shared localStorage →
 * legacy localStorage → scans for any window-scoped `staff_session_*` key from another tab.
 */
export function findStaffSessionStr(): string | null {
    if (typeof window === 'undefined') return null
    const str = sessionStorage.getItem('staff_session')
        || getWindowLocaleStorage('staff_session')
        || localStorage.getItem('staff_session_shared')
        || localStorage.getItem('staff_session')
    if (str) return str
    // Scan for window-scoped keys from other tabs
    try {
        for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i)
            if (!k || !k.startsWith('staff_session_')) continue
            const v = localStorage.getItem(k)
            if (v && v.includes('"role"')) return v
        }
    } catch { /* ignore */ }
    return null
}

/** Remove every staff session key (current tab, shared, and orphaned window-scoped keys). */
export function clearAllStaffSessionStorage() {
    if (typeof window === 'undefined') return
    try {
        sessionStorage.removeItem('staff_session')
        sessionStorage.removeItem('otp_flow_state')
        removeWindowLocaleStorage('staff_session')

        const keysToRemove: string[] = []
        for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i)
            if (!k) continue
            if (k === 'staff_session' || k === 'staff_session_shared' || k.startsWith('staff_session_')) {
                keysToRemove.push(k)
            }
        }
        keysToRemove.forEach((k) => localStorage.removeItem(k))
    } catch {
        /* ignore */
    }
}


// Declare the global variable for TypeScript
declare const __APP_BOOT_ID__: string;

export const STAFF_SESSION_UPDATED_EVENT = 'staff-session-updated'

/** Map staff_users row or JWT metadata permissions to session allow-flags (camelCase). */
function staffPermissionsFromRecord(record: Record<string, unknown> | null | undefined) {
    if (!record) {
        return {
            allowAddClient: false,
            allowSystemSettings: false,
            allowBranding: false,
            allowViewLogs: false,
            allowDashboard: false,
            allowImpersonate: false,
        }
    }
    return {
        allowAddClient: Boolean(record.allowAddClient ?? record.allow_add_client),
        allowSystemSettings: Boolean(record.allowSystemSettings ?? record.allow_system_settings),
        allowBranding: Boolean(record.allowBranding ?? record.allow_branding),
        allowViewLogs: Boolean(record.allowViewLogs ?? record.allow_view_logs),
        allowDashboard: Boolean(record.allowDashboard ?? record.allow_dashboard),
        allowImpersonate: Boolean(record.allowImpersonate ?? record.allow_impersonate),
    }
}

function buildVerifiedStaffSession(
    session: Record<string, unknown>,
    token: string,
    meta: Record<string, unknown>,
): Record<string, unknown> {
    const perms = staffPermissionsFromRecord(
        (meta.permissions as Record<string, unknown>) || session,
    )
    return {
        username: String(meta.staff_username || session.username || '').trim().toLowerCase(),
        email: session.email,
        role: 'SuperAdmin',
        access_token: token,
        refresh_token: session.refresh_token,
        ts: typeof session.ts === 'number' ? session.ts : Date.now(),
        ...perms,
    }
}

function verifyStaffJwtLocally(
    token: string,
    session: Record<string, unknown>,
): Record<string, unknown> | null {
    if (!isStaffSessionJwt(token)) return null
    const payload = jwtPayload(token)
    if (!payload) return null
    const exp = payload.exp
    if (typeof exp === 'number' && exp * 1000 < Date.now()) return null
    const meta = (payload.user_metadata || {}) as Record<string, unknown>
    const isStaff =
        meta.role === 'SuperAdmin' ||
        typeof meta.staff_username === 'string'
    if (!isStaff) return null
    return buildVerifiedStaffSession(session, token, meta)
}

function syncAppBootId() {
    if (typeof window === 'undefined') return
    if (typeof __APP_BOOT_ID__ !== 'undefined') {
        localStorage.setItem('APP_BOOT_ID', __APP_BOOT_ID__)
    }
}

/**
 * Validate staff session via JWT — never trust storage `role` alone.
 * Returns normalized session fields or null if token invalid / not staff.
 */
export async function verifyStaffSessionJwt(staffSessionStr: string | null | undefined): Promise<Record<string, unknown> | null> {
    if (!staffSessionStr) return null
    try {
        const session = JSON.parse(staffSessionStr) as Record<string, unknown>
        const token = String(session.access_token || '').trim()
        if (!token) return null

        const localVerified = verifyStaffJwtLocally(token, session)
        if (!localVerified) return null

        try {
            const { data: { user }, error } = await supabase.auth.getUser(token)
            if (!error && user) {
                const meta = (user.user_metadata || {}) as Record<string, unknown>
                const isStaff =
                    meta.role === 'SuperAdmin' ||
                    typeof meta.staff_username === 'string'
                if (isStaff) {
                    return buildVerifiedStaffSession(session, token, meta)
                }
            }
        } catch {
            // Fall back to locally validated staff JWT (custom HMAC token from staff-login).
        }

        return localVerified
    } catch {
        return null
    }
}

/** Bumped on logout so in-flight permission refreshes cannot restore a cleared session. */
let staffLogoutGeneration = 0

async function getConfiguredStaffEntries(): Promise<StaffListEntry[]> {
    try {
        return await fetchStaffListFromServer()
    } catch (error) {
        console.error('[STAFF-AUTH] Failed to fetch staff settings:', error)
        throw new Error('Staff authentication service unavailable.')
    }
}

function dispatchStaffSessionUpdated() {
    if (typeof window === 'undefined') return
    window.dispatchEvent(new CustomEvent(STAFF_SESSION_UPDATED_EVENT))
}

export const authService = {
    /**
     * @param otpEmailForVerify Optional email that received the OTP (must match `login_otps.email`).
     *        Use the same value you passed to `sendNewOtp` (e.g. profile email / tracking inbox) when it can differ from the sign-in `email` (first argument).
     */
    async login(email: string, password: string, otp?: string, clubId?: string, otpEmailForVerify?: string) {
        const trimmedEmail = (email || '').trim()
        let targetEmail = trimmedEmail
        let bypassOtp = false
        const normalizedIdentifier = trimmedEmail.toLowerCase()

        const resolveEmailFromProfiles = (profiles: Array<{ email?: string | null, full_name?: string | null }> | null | undefined) => {
            const matches = (profiles || []).find((profile) => {
                const profileEmail = String(profile?.email || '').trim().toLowerCase()
                const profileName = String(profile?.full_name || '').trim().toLowerCase()
                return profileEmail === normalizedIdentifier
                    || profileEmail.split('@')[0] === normalizedIdentifier
                    || profileName === normalizedIdentifier
            })

            return matches?.email?.trim() || null
        }

        if (!trimmedEmail.includes('@')) {
            // Short username provided — try a global profile lookup first so
            // users can log in with their display name even without a clubId.
            const { data: profiles } = await supabase
                .from('profiles')
                .select('email, full_name')

            const resolvedGlobalEmail = resolveEmailFromProfiles(profiles)

            if (resolvedGlobalEmail) {
                targetEmail = resolvedGlobalEmail
            } else if (clubId && clubId.trim().toUpperCase() !== 'STAFF') {
                // Club ID login fallback: resolve the username within the selected tenant.
                const { data: tenant } = await supabase
                    .from('tenants')
                    .select('id')
                    .ilike('client_id', clubId.trim())
                    .maybeSingle()

                if (tenant) {
                    const { data: tenantProfiles } = await supabase
                        .from('profiles')
                        .select('email, full_name')
                        .eq('tenant_id', tenant.id)
                    const resolvedTenantEmail = resolveEmailFromProfiles(tenantProfiles)

                    if (resolvedTenantEmail) {
                        targetEmail = resolvedTenantEmail
                    }
                }
            }
            // If still no match, keep as-is (will fail at signIn with a clear error)
        }

        // --- LOGIN ---
        // Always authenticate the exact address entered (or resolved from username).
        // Never rewrite Gmail plus-aliases to a base address.
        const loginEmail = normalizeEmail(targetEmail)
        if (!loginEmail) throw new Error('Email is required')

        const { data, error } = await supabase.auth.signInWithPassword({
            email: loginEmail,
            password,
        })
        if (error) throw error

        const sessionEmail = normalizeEmail(data?.user?.email || '')
        if (!data?.user || sessionEmail !== loginEmail) {
            await supabase.auth.signOut({ scope: 'local' }).catch(() => {})
            clearAuthUser()
            invalidateAuthUser()
            throw new Error('Signed-in account does not match the email entered. Check the full address (including any +tag) and try again.')
        }

        targetEmail = loginEmail
        const authResult = { data, error: null }

        // Invalidate cached auth user so getAuthUser() returns fresh data after sign-in
        invalidateAuthUser()

        // --- SET BOOT ID EARLY ---
        // As soon as signIn succeeds, we mark this boot as "authenticated" locally
        // to prevent checkServerBoot from triggering a logout if it runs while OTP is pending.
        if (typeof window !== 'undefined' && typeof __APP_BOOT_ID__ !== 'undefined') {
            localStorage.setItem('APP_BOOT_ID', __APP_BOOT_ID__);
        }

        if (!otp && !bypassOtp) {
            const otpEnabled = await otpService.isOtpEnabled();
            if (otpEnabled && authResult.data.user) {
                const profile = await this.getProfile(authResult.data.user.id);
                await supabase.auth.signOut({ scope: 'local' }).catch(() => {})
                invalidateAuthUser()
                return { status: 'OTP_PENDING', user: authResult.data.user, profile };
            }
        }

        if (otp && !bypassOtp) {
            const emailForOtp = (otpEmailForVerify && otpEmailForVerify.trim()) || targetEmail
            const verification = await otpService.verifyOtp(emailForOtp, otp);
            if (!verification.valid) throw new Error('Invalid security code.')
        }

        return authResult.data
    },

    /** Updates profile last_login / last_active after a successful sign-in (fire-and-forget). */
    async recordProfileLogin(userId: string) {
        if (!userId) return
        const now = new Date().toISOString()
        try {
            const { error } = await supabase
                .from('profiles')
                .update({
                    last_login: now,
                    last_active: now,
                    status: 'Active',
                    must_change_password: false,
                })
                .eq('id', userId)
            if (error) console.warn('[recordProfileLogin] profile update failed:', error.message)
        } catch (err) {
            console.warn('[recordProfileLogin] unexpected error:', err)
        }
    },

    async verifyStaffCredentials(username: string, password: string): Promise<any> {
        const { data, error } = await invokeStaffLogin({
            action: 'verify-credentials',
            username,
            password,
        })
        if (error) {
            const msg = error.message || ''
            if (/too many attempts/i.test(msg)) throw new Error(msg)
            if (/invalid staff/i.test(msg)) throw new Error('Invalid staff credentials')
            if (/staff password verify failed|server configuration error/i.test(msg)) {
                throw new Error('Staff authentication service unavailable. Contact support if this persists.')
            }
            throw new Error('Staff authentication service unavailable.')
        }
        if (!data?.valid) throw new Error('Invalid staff credentials')
        return data
    },

    async verifyStaffOtp(username: string, email: string, otp: string): Promise<any> {
        const { data, error } = await invokeStaffLogin({
            action: 'verify-otp',
            username,
            email,
            otp,
        })
        if (error) {
            const msg = error.message || ''
            if (/invalid code|invalid security/i.test(msg)) {
                throw new Error('Invalid security code. Please check your email and try again.')
            }
            if (/staff account not found/i.test(msg)) {
                throw new Error('Staff account not found. Sign in again with your username.')
            }
            if (/expired/i.test(msg)) {
                throw new Error('This code has expired. Send a new code to continue.')
            }
            throw new Error(msg || 'Staff verification failed. Please try again.')
        }
        if (data?.session) {
            // Staff sessions use a custom HMAC JWT — not a GoTrue refresh token.
            // Never call setSession; it stores an invalid refresh token and breaks login.
            await supabase.auth.signOut({ scope: 'local' }).catch(() => {})
            invalidateAuthUser()
            clearAuthUser()
            return { success: true, session: data.session }
        }
        return { success: false }
    },

    async logStaffActivity(username: string, email: string) {
        console.log(`[STAFF-LOGIN] User ${username} logged in. Tracking: ${email}`)
        
        const logEntry = {
            tenant_id: '00000000-0000-0000-0000-000000000000',
            action_type: 'SYSTEM_LOGIN',
            status: 'SUCCESS',
            records_processed: 1,
            message: `[MASTER LOGIN] Staff access by: ${username} — Tracking email: ${email} — ${new Date().toLocaleString()}`
        }

        try {
            const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
            // Route through edge function so service role key bypasses RLS (works even without a session)
            const { error } = await invokeAdminAction({ 
                action: 'log-activity', 
                data: { logEntry } 
            })
            if (error) throw error
        } catch (err) {
            // Fallback: direct insert (works if user has an active session)
            const { error } = await supabase.from('integrations_logs').insert(logEntry)
            if (error) console.warn('[STAFF-LOGIN] DB log skipped (no session/RLS):', error.message)
        }
    },

    async changePassword(newPassword: string, currentPassword?: string) {
        const { data: { session } } = await supabase.auth.getSession()
        const email = session?.user?.email?.trim()

        const payload: Record<string, unknown> = {
            action: 'change-password',
            password: newPassword,
        }
        const impersonationTenant = parseImpersonationTenantId(getImpersonateTenantRaw())
        if (impersonationTenant) payload.tenantId = impersonationTenant
        if (currentPassword) {
            payload.data = { currentPassword }
        }

        const { data, error } = await invokeAdminAction(payload)
        if (error) {
            const msg = error.message?.toLowerCase() || ''
            if (msg.includes('same') || msg.includes('different')) {
                throw new Error('You must choose a different password than your current one.')
            }
            if (msg.includes('invalid current password')) {
                throw new Error('Invalid current password')
            }
            throw error
        }

        const result = data as Record<string, unknown> | null
        if (result?.error) {
            throw new Error(String(result.error))
        }

        if (email) await supabase.auth.signInWithPassword({ email, password: newPassword })
        invalidateAuthUser()
    },

    async verifyCurrentPassword(password: string) {
        const { data: { session } } = await supabase.auth.getSession()
        const email = session?.user?.email?.trim()
        if (!email) throw new Error('User not found')

        const { error } = await supabase.auth.signInWithPassword({
            email,
            password,
        })

        if (error) throw new Error('Invalid current password')
        invalidateAuthUser()
        return true
    },

    async logout() {
        staffLogoutGeneration += 1
        clearAllStaffSessionStorage()
        dispatchStaffSessionUpdated()
        clearAuthUser()
        invalidateAuthUser()
        const { error } = await supabase.auth.signOut({ scope: 'global' })
        if (error) throw error
    },

    /** Clear staff session and Supabase auth (used when staff account is deleted or revoked). */
    async forceStaffLogout() {
        staffLogoutGeneration += 1
        clearAllStaffSessionStorage()
        dispatchStaffSessionUpdated()
        clearAuthUser()
        invalidateAuthUser()
        try {
            await supabase.auth.signOut({ scope: 'global' })
        } catch {
            /* ignore — staff-only sessions may have no Supabase session */
        }
    },

    async getCurrentUser() {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session) return null
        return session.user
    },

    async getProfile(userId?: string) {
        let targetId = userId
        if (!targetId) {
            const user = await getAuthUser()
            if (!user) return null
            targetId = user.id
        }

        const { data, error } = await supabase
            .from('profiles')
            .select('*, tenant:tenants(*)')
            .eq('id', targetId)
            .maybeSingle()

        if (error && error.code !== 'PGRST116') {
            console.error('Error fetching profile:', error)
            return null
        }
        return data
    },

    async updateThemeSettings(settings: any) {
        const user = await getAuthUser()
        if (!user) return

        const { error } = await supabase
            .from('profiles')
            .update({ theme_settings: settings })
            .eq('id', user.id)

        if (error) {
            console.error('Failed to save theme settings', error)
            throw error
        }
    },

    async mergeThemeSettings(partial: Record<string, unknown>) {
        const user = await getAuthUser()
        if (!user) return
        const profile = await this.getProfile(user.id)
        if (!profile) return
        const cur = profile.theme_settings && typeof profile.theme_settings === 'object' && !Array.isArray(profile.theme_settings)
            ? { ...(profile.theme_settings as Record<string, unknown>) }
            : {}
        await this.updateThemeSettings({ ...cur, ...partial })
    },

    async setActivityFeedColumns(n: number) {
        if (!Number.isFinite(n) || n < 1 || n > 4) return
        const rounded = Math.round(n)
        if (rounded < 1 || rounded > 4) return
        await this.mergeThemeSettings({ activity_feed_columns: rounded })
    },

    async getCurrentTenantId() {
        const impersonationRaw = getImpersonateTenantRaw()
        if (impersonationRaw) {
            const user = await this.getCurrentUser()
            const profile = user ? await this.getProfile(user.id) : null
            const authorizedImpersonation = await resolveAuthorizedImpersonation({
                profileTenantId: profile?.tenant_id || null,
                profileRole: profile?.role || null,
                staffSessionRaw: findStaffSessionStr(),
            })

            if (authorizedImpersonation) {
                return authorizedImpersonation.tenantId
            }

            const parsedFallback = parseImpersonationTenantId(impersonationRaw)
            const impersonationSource = getImpersonationSource()
            if (parsedFallback && (findStaffSessionStr() || impersonationSource === 'control' || impersonationSource === 'admin')) {
                console.warn('[auth] Impersonation auth check failed; using stored tenant id')
                return parsedFallback
            }

            clearImpersonationStorage()
            return profile?.tenant_id || null
        }

        const user = await this.getCurrentUser();
        if (!user) return null;
        const profile = await this.getProfile(user.id);
        return profile?.tenant_id;
    },

    async getImpersonationAuthParams(): Promise<{
        profileTenantId: string | null
        profileRole: string | null
        staffSessionRaw: string | null
    }> {
        let profileTenantId: string | null = null
        let profileRole: string | null = null
        try {
            const user = await this.getCurrentUser()
            if (user) {
                const profile = await this.getProfile(user.id)
                profileTenantId = profile?.tenant_id ?? null
                profileRole = profile?.role ?? null
            }
        } catch {
            /* staff-only session */
        }
        return {
            profileTenantId,
            profileRole,
            staffSessionRaw: findStaffSessionStr(),
        }
    },

    async checkServerBoot() {
        if (typeof window === 'undefined') return;
        const currentBootId = typeof __APP_BOOT_ID__ !== 'undefined' ? __APP_BOOT_ID__ : null;
        if (!currentBootId) return;

        const lastBootId = localStorage.getItem('APP_BOOT_ID');
        if (lastBootId && lastBootId !== currentBootId) {
            const { data: { session } } = await supabase.auth.getSession();
            const hasStaffSession = !!findStaffSessionStr();
            if (session || hasStaffSession) {
                console.log('Server restart detected, logging out...');
                await this.forceStaffLogout();
                localStorage.setItem('APP_BOOT_ID', currentBootId);
                window.location.href = '/login';
                return true;
            }
        }
        localStorage.setItem('APP_BOOT_ID', currentBootId);
        return false;
    },

    setStaffSession(session: Record<string, any> | null, options?: { force?: boolean }) {
        if (typeof window === 'undefined') return

        if (!session) {
            clearAllStaffSessionStorage()
            dispatchStaffSessionUpdated()
            return
        }

        const sessionStr = JSON.stringify(session)
        const existing = this.getStaffSession()
        if (
            !options?.force &&
            existing &&
            staffSessionPermissionSignature(existing) === staffSessionPermissionSignature(session)
        ) {
            return
        }

        sessionStorage.setItem('staff_session', sessionStr)
        setWindowLocaleStorage('staff_session', sessionStr)
        // Write to a shared key so other tabs can pick up the staff session
        localStorage.setItem('staff_session_shared', sessionStr)
        syncAppBootId()
        dispatchStaffSessionUpdated()
    },

    async refreshCurrentStaffSessionPermissions() {
        const refreshGeneration = staffLogoutGeneration
        const raw = findStaffSessionStr()
        const verified = await verifyStaffSessionJwt(raw)
        if (!verified) {
            this.setStaffSession(null)
            return null
        }

        const currentSession = this.getStaffSession()
        const sessionLogin = currentSession?.username || currentSession?.email || verified.username || verified.email
        if (!sessionLogin) return null

        try {
            const allowedUsernames = await getConfiguredStaffEntries()
            if (refreshGeneration !== staffLogoutGeneration || !findStaffSessionStr()) {
                return null
            }

            const staffUser = findStaffEntry(allowedUsernames, sessionLogin)

            if (!staffUser) {
                if (verified?.username) {
                    const nextSession = {
                        ...(currentSession || {}),
                        ...verified,
                        ts: Date.now(),
                    }
                    if (refreshGeneration !== staffLogoutGeneration || !findStaffSessionStr()) {
                        return null
                    }
                    this.setStaffSession(nextSession, { force: true })
                    return nextSession
                }
                this.setStaffSession(null)
                return null
            }

            const refreshedSession = buildStaffSessionFromEntry(currentSession, staffUser)
            const nextSession = { ...refreshedSession, ts: Date.now() }
            if (refreshGeneration !== staffLogoutGeneration || !findStaffSessionStr()) {
                return null
            }
            this.setStaffSession(nextSession, { force: true })
            return nextSession
        } catch (error) {
            console.warn('[STAFF-AUTH] Failed to refresh current staff session permissions:', error)
            return refreshGeneration === staffLogoutGeneration ? currentSession : null
        }
    },

    getStaffSession() {
        const str = findStaffSessionStr()
        if (!str) return null;
        try {
            return JSON.parse(str);
        } catch (e) {
            return null;
        }
    }
}
